import { useEffect, useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

export type LeaseMember = {
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  ontraport_contact_id: string | null;
  una_name: string | null;
  suite_number: number | null;
};

type LeasePanelProps = {
  member: LeaseMember;
  onPoll: () => void;
};

/**
 * The suite lease signing panel. The link is built by the server from the
 * member's own record, so nothing here is taken from the page.
 */
export function LeasePanel({ onPoll }: LeasePanelProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    const id = window.setInterval(onPoll, 5000);
    return () => window.clearInterval(id);
  }, [onPoll]);

  useEffect(() => {
    let active = true;
    void (async () => {
      try {
        const { data, error } = await supabase.functions.invoke("lease-sign-url", { body: {} });
        if (!active) return;
        const failure = error?.message || (data as any)?.error;
        if (failure || !(data as any)?.url) {
          setProblem("We could not open your lease just yet. Please refresh in a moment.");
          return;
        }
        setUrl(String((data as any).url));
      } catch (_e) {
        if (active) setProblem("We could not open your lease just yet. Please refresh in a moment.");
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (problem) {
    return (
      <div className="rounded-lg border border-border/70 p-4 text-sm text-muted-foreground">{problem}</div>
    );
  }

  if (!url) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border/70 p-4 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Getting your lease ready
      </div>
    );
  }

  return (
    <div className="space-y-4 rounded-lg border border-border/70 p-4">
      <Button asChild className="min-h-11 w-full">
        <a href={url}>Read and sign your lease</a>
      </Button>

      <p className="text-sm text-muted-foreground">
        The lease opens full screen. When you finish signing, we bring you right back here.
      </p>
    </div>
  );
}

export function LeaseSignedCard() {
  return (
    <div className="flex items-start gap-3 rounded-lg border border-border/70 bg-muted/40 p-4">
      <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
      <div>
        <p className="font-medium">Your lease is signed. Thank you.</p>
      </div>
    </div>
  );
}
